import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Switch,
  Table,
  Tag,
  Tabs,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  createSuperAdminClinicUser,
  getSuperAdminClinic,
  getSuperAdminClinicConfig,
  getSuperAdminClinicUsers,
  getSuperAdminRoles,
  updateSuperAdminClinic,
  updateSuperAdminClinicConfig,
  updateSuperAdminClinicPlan,
  updateSuperAdminClinicUser,
} from "../../apis/superadmin";
import { format } from "date-fns";

const ORG_TYPE_OPTIONS = [
  { value: "clinic", label: "Clinic" },
  { value: "hospital", label: "Hospital" },
  { value: "diagnostic_center", label: "Diagnostic Center" },
  { value: "nursing_home", label: "Nursing Home" },
  { value: "other", label: "Other" },
];

const asBool = (v: any) =>
  v === true || v === "true" || v === 1 || v === "1";

const asYesNo = (v: any) => (v === "yes" ? true : false);

const formatRoleLabel = (name: string) => {
  const labels: Record<string, string> = {
    admin: "Admin",
    doctor: "Doctor",
    nurse: "Nurse",
    receptionist: "Receptionist (Front Desk)",
    manager: "Manager",
    pharmacist: "Pharmacist",
    lab_manager: "Lab Manager (Pathologist)",
  };
  return labels[name] ?? name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const ClinicDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [clinic, setClinic] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [availableRoles, setAvailableRoles] = useState<
    Array<{ id: number; name: string; label: string }>
  >([]);
  const [config, setConfig] = useState<Record<string, any>>({});
  const [loadingClinic, setLoadingClinic] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [billing, setBilling] = useState<Record<string, any>>({});
  const [savingBilling, setSavingBilling] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [detailsForm] = Form.useForm();

  const clinicId = Number(id);

  const syncDetailsForm = (clinicData: any, configData: Record<string, any> = {}) => {
    detailsForm.setFieldsValue({
      name: clinicData.name ?? "",
      email: clinicData.email ?? "",
      phone: clinicData.phone ?? "",
      address: clinicData.address ?? "",
      zipCode: clinicData.zipCode ?? "",
      country: clinicData.country ?? "India",
      organizationType:
        configData.organizationType ??
        configData.organization_type ??
        "clinic",
    });
  };

  useEffect(() => {
    setLoadingClinic(true);
    setLoadingConfig(true);

    Promise.all([
      getSuperAdminClinic(clinicId),
      getSuperAdminClinicConfig(clinicId),
    ])
      .then(([clinicRes, configRes]: any[]) => {
        const clinicData = clinicRes.data;
        const configData = configRes.data;
        setClinic(clinicData);
        setConfig(configData);
        setBilling({
          plan:                clinicData.plan,
          numberOfVisits:      clinicData.numberOfVisits ?? null,
          whatsappBalance:     clinicData.whatsappBalance ?? 0,
          textMessageBalance:  clinicData.textMessageBalance ?? 0,
          patientLimit:        clinicData.patientLimit ?? null,
        });
        syncDetailsForm(clinicData, configData);
      })
      .finally(() => {
        setLoadingClinic(false);
        setLoadingConfig(false);
      });

    fetchUsers();
    getSuperAdminRoles()
      .then((res: any) => {
        const roles = (res.data ?? []).map((r: any) => ({
          id: r.id,
          name: r.name,
          label: r.label || formatRoleLabel(r.name),
        }));
        setAvailableRoles(roles);
      })
      .catch(() => setAvailableRoles([]));
  }, [clinicId]);

  const fetchUsers = () => {
    setLoadingUsers(true);
    getSuperAdminClinicUsers(clinicId)
      .then((res: any) => setUsers(res.data))
      .finally(() => setLoadingUsers(false));
  };

  const handleAddUser = (values: any) => {
    setSubmitting(true);
    createSuperAdminClinicUser(clinicId, {
      ...values,
      isActive: values.isActive ?? true,
    })
      .then(() => {
        setShowAddUser(false);
        form.resetFields();
        message.success("User created");
        fetchUsers();
      })
      .catch((err: any) => {
        const apiErrors = err?.response?.data?.errors;
        if (apiErrors) {
          const first = Object.values(apiErrors).flat()[0];
          message.error(typeof first === "string" ? first : "Failed to create user");
        } else {
          message.error(err?.response?.data?.message || "Failed to create user");
        }
      })
      .finally(() => setSubmitting(false));
  };

  const openEditUser = (user: any) => {
    setEditingUser(user);
    editForm.setFieldsValue({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone ?? "",
      roles: user.roles ?? [],
      isActive: Boolean(user.isActive),
    });
  };

  const handleEditUser = (values: any) => {
    if (!editingUser?.id) return;
    setSubmitting(true);
    updateSuperAdminClinicUser(clinicId, editingUser.id, {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone || null,
      roles: values.roles,
      isActive: values.isActive,
    })
      .then(() => {
        message.success("User updated");
        setEditingUser(null);
        editForm.resetFields();
        fetchUsers();
      })
      .catch((err: any) => {
        const apiErrors = err?.response?.data?.errors;
        if (apiErrors) {
          const first = Object.values(apiErrors).flat()[0];
          message.error(typeof first === "string" ? first : "Failed to update user");
        } else {
          message.error(err?.response?.data?.message || "Failed to update user");
        }
      })
      .finally(() => setSubmitting(false));
  };

  const handleToggleUserActive = (user: any, isActive: boolean) => {
    updateSuperAdminClinicUser(clinicId, user.id, { isActive })
      .then(() => {
        setUsers((prev) =>
          prev.map((u) => (u.id === user.id ? { ...u, isActive } : u))
        );
        message.success(isActive ? "User activated" : "User deactivated");
      })
      .catch(() => message.error("Failed to update user status"));
  };

  const setConfigVal = (code: string, value: any) =>
    setConfig((prev) => ({ ...prev, [code]: value }));

  const applyApiErrorsToForm = (
    formInstance: typeof detailsForm,
    apiErrors: Record<string, string[] | string>
  ) => {
    const fieldErrors = Object.entries(apiErrors).map(([key, messages]) => {
      const name = key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
      const msg = Array.isArray(messages) ? messages[0] : String(messages);
      return { name, errors: [msg] };
    });
    formInstance.setFields(fieldErrors);
  };

  const handleSaveDetails = (values: any) => {
    setSavingDetails(true);
    detailsForm.setFields(
      ["name", "email", "phone", "address", "zipCode", "country", "organizationType"].map(
        (name) => ({ name, errors: [] })
      )
    );

    updateSuperAdminClinic(clinicId, {
      name: values.name,
      email: values.email,
      phone: values.phone,
      address: values.address,
      zipCode: values.zipCode,
      country: values.country,
      organizationType: values.organizationType,
    })
      .then((res: any) => {
        const updatedClinic = res.data.clinic ?? res.data;
        const updatedConfig = res.data.config ?? config;
        setClinic(updatedClinic);
        if (res.data.config) setConfig(res.data.config);
        syncDetailsForm(updatedClinic, updatedConfig);
        message.success("Clinic details updated");
      })
      .catch((err: any) => {
        const apiErrors = err?.response?.data?.errors;
        if (apiErrors && typeof apiErrors === "object") {
          applyApiErrorsToForm(detailsForm, apiErrors);
          const firstMessage = Object.values(apiErrors)
            .flat()
            .find(Boolean);
          message.error(
            typeof firstMessage === "string"
              ? firstMessage
              : "Please fix the highlighted fields"
          );
        } else {
          message.error(
            err?.response?.data?.message || "Failed to update clinic details"
          );
        }
      })
      .finally(() => setSavingDetails(false));
  };

  const handleSaveBilling = () => {
    setSavingBilling(true);
    const payload: Record<string, any> = { plan: billing.plan };
    if (billing.plan === "flexivisit") payload.number_of_visits = billing.numberOfVisits;
    if (billing.plan === "free") payload.patient_limit = billing.patientLimit;
    payload.whatsapp_balance     = billing.whatsappBalance;
    payload.text_message_balance = billing.textMessageBalance;

    updateSuperAdminClinicPlan(clinicId, payload)
      .then((res: any) => {
        setClinic((prev: any) => ({ ...prev, ...res.data }));
        message.success("Plan & billing updated");
      })
      .catch(() => message.error("Failed to update"))
      .finally(() => setSavingBilling(false));
  };

  const handleSaveConfig = () => {
    setSavingConfig(true);
    updateSuperAdminClinicConfig(clinicId, config)
      .then(() => message.success("Configuration saved"))
      .catch(() => message.error("Failed to save configuration"))
      .finally(() => setSavingConfig(false));
  };

  const userColumns: ColumnsType<any> = [
    { title: "Name", render: (_, row) => `${row.firstName} ${row.lastName}` },
    { title: "Email", dataIndex: "email" },
    { title: "Phone", dataIndex: "phone", render: (v) => v ?? "—" },
    {
      title: "Roles",
      dataIndex: "roles",
      render: (roles: string[]) =>
        roles?.map((r) => <Tag key={r}>{formatRoleLabel(r)}</Tag>),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      width: 120,
      render: (v, row) => (
        <Switch
          checked={Boolean(v)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          onChange={(checked) => handleToggleUserActive(row, checked)}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      render: (_, row) => (
        <Button type="link" className="!px-0" onClick={() => openEditUser(row)}>
          Edit
        </Button>
      ),
    },
  ];

  if (loadingClinic) return <div className="p-8 text-gray-500">Loading...</div>;
  if (!clinic) return <div className="p-8 text-red-500">Clinic not found.</div>;

  const ConfigSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="mb-6">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{title}</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
    </div>
  );

  const ConfigRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
      <span className="text-sm text-gray-700">{label}</span>
      <div>{children}</div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate("/superadmin")}
          className="text-sm text-gray-500 hover:text-gray-800"
        >
          ← Clinics
        </button>
        <span className="text-gray-300">/</span>
        <h1 className="text-xl font-bold text-gray-900">{clinic.name}</h1>
        <Tag color={clinic.isActive ? "success" : "error"}>
          {clinic.isActive ? "Active" : "Inactive"}
        </Tag>
      </div>

      {/* Clinic Overview / Editable Details */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Clinic Details</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Update identity and contact information for this clinic.
            </p>
          </div>
          <Tag className="m-0 w-fit" color={clinic.plan === "free" ? "default" : clinic.plan === "essential" ? "blue" : "green"}>
            {clinic.plan?.toUpperCase()} PLAN
          </Tag>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-gray-100 border-b border-gray-100 bg-slate-50/80">
          {[
            { label: "Users", value: clinic.usersCount ?? clinic.users?.length ?? "—" },
            { label: "Patients", value: clinic.patientsCount ?? "—" },
            { label: "Visits", value: clinic.visitsCount ?? "—" },
            {
              label: "Registered",
              value: clinic.createdAt ? format(new Date(clinic.createdAt), "dd MMM yyyy") : "—",
            },
          ].map((stat) => (
            <div key={stat.label} className="px-5 py-4">
              <div className="text-[11px] font-medium uppercase tracking-wider text-gray-400">
                {stat.label}
              </div>
              <div className="mt-1 text-lg font-semibold text-gray-900 tabular-nums">
                {stat.value}
              </div>
            </div>
          ))}
        </div>

        <Form
          form={detailsForm}
          layout="vertical"
          onFinish={handleSaveDetails}
          requiredMark="optional"
          className="px-6 py-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
            <Form.Item
              name="name"
              label={<span className="text-sm font-medium text-gray-700">Clinic Name</span>}
              rules={[{ required: true, message: "Name is required" }]}
              required
            >
              <Input size="large" placeholder="e.g. Dhanvantaree Clinic" className="rounded-lg" />
            </Form.Item>
            <Form.Item
              name="organizationType"
              label={<span className="text-sm font-medium text-gray-700">Type</span>}
              rules={[{ required: true, message: "Type is required" }]}
              required
            >
              <Select
                size="large"
                options={ORG_TYPE_OPTIONS}
                showSearch
                allowClear={false}
                className="w-full"
                placeholder="Select type"
              />
            </Form.Item>
            <Form.Item
              name="phone"
              label={<span className="text-sm font-medium text-gray-700">Phone</span>}
              rules={[
                { required: true, message: "Phone is required" },
                { len: 10, message: "Must be 10 digits" },
              ]}
              required
            >
              <Input size="large" maxLength={10} placeholder="10-digit mobile" className="rounded-lg" />
            </Form.Item>
            <Form.Item
              name="email"
              label={<span className="text-sm font-medium text-gray-700">Email</span>}
              rules={[{ required: true, type: "email", message: "Valid email required" }]}
              required
            >
              <Input size="large" placeholder="clinic@example.com" className="rounded-lg" />
            </Form.Item>
            <Form.Item
              name="address"
              label={<span className="text-sm font-medium text-gray-700">Address</span>}
              className="md:col-span-2"
              rules={[{ required: true, message: "Address is required" }]}
              required
            >
              <Input.TextArea
                rows={2}
                placeholder="Street, area, city"
                className="rounded-lg"
                autoSize={{ minRows: 2, maxRows: 3 }}
              />
            </Form.Item>
            <Form.Item
              name="zipCode"
              label={<span className="text-sm font-medium text-gray-700">ZIP Code</span>}
            >
              <Input size="large" placeholder="e.g. 416001" className="rounded-lg" />
            </Form.Item>
            <Form.Item
              name="country"
              label={<span className="text-sm font-medium text-gray-700">Country</span>}
            >
              <Input size="large" placeholder="India" className="rounded-lg" />
            </Form.Item>
          </div>

          <div className="flex justify-end pt-4 mt-2 border-t border-gray-100">
            <Button type="primary" htmlType="submit" loading={savingDetails} size="large" className="rounded-lg px-6">
              Save Details
            </Button>
          </div>
        </Form>
      </div>

      <Tabs
        defaultActiveKey="config"
        items={[
          {
            key: "config",
            label: "Configuration",
            children: (
              <div className="bg-white rounded-lg shadow-sm p-6">
                {loadingConfig ? (
                  <div className="text-gray-400 text-sm">Loading config...</div>
                ) : (
                  <>
                    {/* Modules */}
                    <ConfigSection title="Modules">
                      <ConfigRow label="Pharmacy module">
                        <Switch
                          checked={asBool(config.pharmacyModule ?? config.pharmacy_module)}
                          onChange={(v) => {
                            setConfigVal("pharmacy_module", v);
                            setConfigVal("pharmacyModule", v);
                          }}
                        />
                      </ConfigRow>
                      <ConfigRow label="Pathology / Lab module">
                        <Switch
                          checked={asBool(config.pathologyModule ?? config.pathology_module)}
                          onChange={(v) => {
                            setConfigVal("pathology_module", v);
                            setConfigVal("pathologyModule", v);
                          }}
                        />
                      </ConfigRow>
                      <ConfigRow label="IPD module">
                        <Switch
                          checked={asBool(config.ipdModule ?? config.ipd_module)}
                          onChange={(v) => {
                            setConfigVal("ipd_module", v);
                            setConfigVal("ipdModule", v);
                          }}
                        />
                      </ConfigRow>
                    </ConfigSection>

                    {/* Appointment & Hours */}
                    <ConfigSection title="Appointments & Hours">
                      <ConfigRow label="Appointment window (mins)">
                        <InputNumber
                          min={5}
                          max={120}
                          step={5}
                          value={Number(config.appointmentWindow ?? config.appointment_window ?? 20)}
                          onChange={(v) => {
                            setConfigVal("appointment_window", v);
                            setConfigVal("appointmentWindow", v);
                          }}
                          style={{ width: 90 }}
                        />
                      </ConfigRow>
                      <ConfigRow label="Open 24 hours">
                        <Switch
                          checked={asYesNo(config.isOpen24Hours ?? config.is_open_24_hours)}
                          onChange={(v) => {
                            setConfigVal("is_open_24_hours", v ? "yes" : "no");
                            setConfigVal("isOpen24Hours", v ? "yes" : "no");
                          }}
                        />
                      </ConfigRow>
                      <ConfigRow label="Open time">
                        <Input
                          style={{ width: 110 }}
                          value={config.openTime ?? config.open_time ?? ""}
                          onChange={(e) => {
                            setConfigVal("open_time", e.target.value);
                            setConfigVal("openTime", e.target.value);
                          }}
                          placeholder="9:00 am"
                        />
                      </ConfigRow>
                      <ConfigRow label="Close time">
                        <Input
                          style={{ width: 110 }}
                          value={config.closeTime ?? config.close_time ?? ""}
                          onChange={(e) => {
                            setConfigVal("close_time", e.target.value);
                            setConfigVal("closeTime", e.target.value);
                          }}
                          placeholder="7:00 pm"
                        />
                      </ConfigRow>
                      <ConfigRow label="Weekly off days">
                        <Input
                          style={{ width: 160 }}
                          value={config.weeklyOffDays ?? config.weekly_off_days ?? ""}
                          onChange={(e) => {
                            setConfigVal("weekly_off_days", e.target.value);
                            setConfigVal("weeklyOffDays", e.target.value);
                          }}
                          placeholder="Sunday"
                        />
                      </ConfigRow>
                    </ConfigSection>

                    {/* Notifications */}
                    <ConfigSection title="Notifications">
                      <ConfigRow label="Notification medium">
                        <Select
                          style={{ width: 130 }}
                          value={config.notificationMedium ?? config.notification_medium ?? "both"}
                          onChange={(v) => {
                            setConfigVal("notification_medium", v);
                            setConfigVal("notificationMedium", v);
                          }}
                          options={[
                            { value: "both", label: "Both" },
                            { value: "sms", label: "SMS only" },
                            { value: "whatsapp", label: "WhatsApp only" },
                          ]}
                        />
                      </ConfigRow>
                      <ConfigRow label="Send follow-up message">
                        <Switch
                          checked={asYesNo(config.sendFollowUpMessage ?? config.send_follow_up_message)}
                          onChange={(v) => {
                            setConfigVal("send_follow_up_message", v ? "yes" : "no");
                            setConfigVal("sendFollowUpMessage", v ? "yes" : "no");
                          }}
                        />
                      </ConfigRow>
                      <ConfigRow label="Send thank-you message">
                        <Switch
                          checked={asYesNo(config.sendThankYouMessage ?? config.send_thank_you_message)}
                          onChange={(v) => {
                            setConfigVal("send_thank_you_message", v ? "yes" : "no");
                            setConfigVal("sendThankYouMessage", v ? "yes" : "no");
                          }}
                        />
                      </ConfigRow>
                      <div className="sm:col-span-2 lg:col-span-3">
                        <ConfigRow label="Google review link">
                          <Input
                            style={{ width: 320 }}
                            value={config.googleReviewLink ?? config.google_review_link ?? ""}
                            onChange={(e) => {
                              setConfigVal("google_review_link", e.target.value);
                              setConfigVal("googleReviewLink", e.target.value);
                            }}
                            placeholder="https://g.page/r/..."
                          />
                        </ConfigRow>
                      </div>
                    </ConfigSection>

                    {/* Prescription */}
                    <ConfigSection title="Prescription">
                      <ConfigRow label="Doctor on prescription">
                        <Select
                          style={{ width: 175 }}
                          value={config.doctorOnPrescription ?? config.doctor_on_prescription ?? "logged_in_user"}
                          onChange={(v) => {
                            setConfigVal("doctor_on_prescription", v);
                            setConfigVal("doctorOnPrescription", v);
                          }}
                          options={[
                            { value: "logged_in_user", label: "Logged-in user" },
                            { value: "treating_doctor", label: "Treating doctor" },
                          ]}
                        />
                      </ConfigRow>
                    </ConfigSection>

                    <div className="flex justify-end pt-2 border-t border-gray-100">
                      <Button
                        type="primary"
                        loading={savingConfig}
                        onClick={handleSaveConfig}
                      >
                        Save Configuration
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ),
          },
          {
            key: "billing",
            label: "Plan & Billing",
            children: (
              <div className="bg-white rounded-lg shadow-sm p-6 max-w-xl">
                <ConfigSection title="Plan">
                  <div className="sm:col-span-2 lg:col-span-3">
                    <ConfigRow label="Current plan">
                      <Select
                        style={{ width: 160 }}
                        value={billing.plan}
                        onChange={(v) => setBilling((b: any) => ({ ...b, plan: v }))}
                        options={[
                          { value: "free",        label: "Free" },
                          { value: "essential",   label: "Essential" },
                          { value: "flexivisit",  label: "FlexiVisit" },
                        ]}
                      />
                    </ConfigRow>
                  </div>
                  {billing.plan === "flexivisit" && (
                    <div className="sm:col-span-2 lg:col-span-3">
                      <ConfigRow label="Visit balance (remaining)">
                        <InputNumber
                          min={0}
                          style={{ width: 130 }}
                          value={billing.numberOfVisits}
                          onChange={(v) => setBilling((b: any) => ({ ...b, numberOfVisits: v }))}
                          placeholder="e.g. 500"
                        />
                      </ConfigRow>
                    </div>
                  )}
                  {billing.plan === "free" && (
                    <div className="sm:col-span-2 lg:col-span-3">
                      <ConfigRow label="Patient limit">
                        <InputNumber
                          min={0}
                          style={{ width: 130 }}
                          value={billing.patientLimit}
                          onChange={(v) => setBilling((b: any) => ({ ...b, patientLimit: v }))}
                          placeholder="e.g. 50"
                        />
                      </ConfigRow>
                    </div>
                  )}
                </ConfigSection>

                <ConfigSection title="Message Balances">
                  <div className="sm:col-span-2 lg:col-span-3">
                    <ConfigRow label="WhatsApp balance">
                      <InputNumber
                        min={0}
                        style={{ width: 130 }}
                        value={billing.whatsappBalance}
                        onChange={(v) => setBilling((b: any) => ({ ...b, whatsappBalance: v }))}
                        placeholder="e.g. 1000"
                      />
                    </ConfigRow>
                  </div>
                  <div className="sm:col-span-2 lg:col-span-3">
                    <ConfigRow label="SMS balance">
                      <InputNumber
                        min={0}
                        style={{ width: 130 }}
                        value={billing.textMessageBalance}
                        onChange={(v) => setBilling((b: any) => ({ ...b, textMessageBalance: v }))}
                        placeholder="e.g. 500"
                      />
                    </ConfigRow>
                  </div>
                </ConfigSection>

                <div className="flex justify-end pt-2 border-t border-gray-100">
                  <Button type="primary" loading={savingBilling} onClick={handleSaveBilling}>
                    Save Plan & Billing
                  </Button>
                </div>
              </div>
            ),
          },
          {
            key: "users",
            label: "Users",
            children: (
              <div>
                <div className="flex justify-end mb-4">
                  <Button type="primary" onClick={() => setShowAddUser(true)}>
                    + Add User
                  </Button>
                </div>
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <Table
                    rowKey="id"
                    dataSource={users}
                    columns={userColumns}
                    loading={loadingUsers}
                    pagination={false}
                  />
                </div>
              </div>
            ),
          },
        ]}
      />

      <Modal
        title="Add User to Clinic"
        open={showAddUser}
        onCancel={() => { setShowAddUser(false); form.resetFields(); }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddUser}
          className="mt-2"
          initialValues={{ isActive: true }}
        >
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </div>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: "email" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="Phone (10 digits)">
            <Input maxLength={10} />
          </Form.Item>
          <Form.Item name="roles" label="Roles" rules={[{ required: true }]}>
            <Select mode="multiple" placeholder="Select roles" optionFilterProp="label">
              {availableRoles.map((r) => (
                <Select.Option key={r.name} value={r.name} label={r.label}>
                  {r.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="isActive" label="Status" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
          <div className="flex justify-end gap-2 mt-2">
            <Button onClick={() => { setShowAddUser(false); form.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>Create User</Button>
          </div>
        </Form>
      </Modal>

      <Modal
        title="Edit User"
        open={Boolean(editingUser)}
        onCancel={() => { setEditingUser(null); editForm.resetFields(); }}
        footer={null}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditUser} className="mt-2">
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </div>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: "email" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="Phone (10 digits)">
            <Input maxLength={10} />
          </Form.Item>
          <Form.Item name="roles" label="Roles" rules={[{ required: true }]}>
            <Select mode="multiple" placeholder="Select roles" optionFilterProp="label">
              {availableRoles.map((r) => (
                <Select.Option key={r.name} value={r.name} label={r.label}>
                  {r.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="isActive" label="Status" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
          <div className="flex justify-end gap-2 mt-2">
            <Button onClick={() => { setEditingUser(null); editForm.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>Save Changes</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ClinicDetail;
